import { createTheme, alpha } from '@mui/material/styles';

// Color Palette
const colors = {
    primary: {
        main: '#6C63FF', // Electric Violet
        light: '#8F88FF',
        dark: '#4B44CC',
        contrastText: '#ffffff',
    },
    secondary: {
        main: '#FF6584', // Hot Pink / Coral
        light: '#FF8FA3',
        dark: '#CC4060',
        contrastText: '#ffffff',
    },
    background: {
        default: '#0B0F19', // Deep Midnight Blue
        paper: '#1A1F2E',   // Slightly lighter for cards (fallback)
    },
    text: {
        primary: '#F3F4F6',
        secondary: '#9CA3AF',
    },
    success: {
        main: '#00E096',
    },
    warning: {
        main: '#FFB946',
    },
    error: {
        main: '#FF3D71',
    },
    info: {
        main: '#2F80ED',
    },
};

const partyTheme = createTheme({
    palette: {
        mode: 'dark',
        ...colors,
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 700,
        },
        h2: {
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 700,
        },
        h3: {
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 600,
        },
        h4: {
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 600,
        },
        h5: {
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 500,
        },
        h6: {
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 500,
        },
        button: {
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 600,
            textTransform: 'none', // No all-caps buttons
        },
    },
    shape: {
        borderRadius: 16, // More rounded corners generally
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: colors.background.default,
                    backgroundImage: `
            radial-gradient(at 0% 0%, ${alpha(colors.primary.main, 0.15)} 0px, transparent 50%),
            radial-gradient(at 100% 0%, ${alpha(colors.secondary.main, 0.15)} 0px, transparent 50%),
            radial-gradient(at 100% 100%, ${alpha(colors.primary.main, 0.15)} 0px, transparent 50%),
            radial-gradient(at 0% 100%, ${alpha(colors.secondary.main, 0.15)} 0px, transparent 50%)
          `,
                    backgroundAttachment: 'fixed',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 50, // Pill shape
                    padding: '10px 24px',
                    boxShadow: 'none',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 20px -4px ${alpha(colors.primary.main, 0.5)}`,
                    },
                    '&:active': {
                        transform: 'translateY(0)',
                    },
                },
                containedPrimary: {
                    background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                },
                containedSecondary: {
                    background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none', // Remove default MUI overlay
                    backgroundColor: alpha(colors.background.paper, 0.6), // Semi-transparent
                    backdropFilter: 'blur(12px)', // Glass effect
                    border: `1px solid ${alpha('#ffffff', 0.08)}`,
                },
                elevation1: {
                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 24,
                    overflow: 'visible', // Allow hover effects to go outside
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `0 12px 40px -8px ${alpha('#000000', 0.3)}`,
                        borderColor: alpha(colors.primary.main, 0.3),
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha(colors.background.default, 0.8),
                    backdropFilter: 'blur(12px)',
                    borderBottom: `1px solid ${alpha('#ffffff', 0.08)}`,
                    boxShadow: 'none',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    backdropFilter: 'blur(8px)',
                },
                filled: {
                    border: `1px solid ${alpha('#ffffff', 0.05)}`,
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha('#ffffff', 0.03),
                    backdropFilter: 'blur(4px)',
                    borderRadius: 12,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: alpha('#ffffff', 0.05),
                    },
                    '&.Mui-focused': {
                        backgroundColor: alpha('#ffffff', 0.08),
                        boxShadow: `0 0 0 2px ${alpha(colors.primary.main, 0.2)}`,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#ffffff', 0.1),
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(colors.primary.main, 0.5),
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.primary.main,
                        borderWidth: 1, // Keep it thin, let the box-shadow do the glowing
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: alpha('#ffffff', 0.7),
                    '&.Mui-focused': {
                        color: colors.primary.main,
                    },
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: alpha(colors.background.paper, 0.8),
                    backdropFilter: 'blur(8px)',
                    border: `1px solid ${alpha('#ffffff', 0.1)}`,
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: '0.875rem',
                },
            },
        },
    },
});

export default partyTheme;
